import { type FC, useCallback, useEffect, useRef, useState } from "react";

import Typography from "@/design-system/Typography";
import ChevronLeft from "@/design-system/icons/chevron-left";
import ChevronRight from "@/design-system/icons/chevron-right";
import { type ITelemetryCustom, sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { FlyerCarouselResources } from "@/src/homepage/Flyers/FlyerCarousel.Resources";
import { FlyerCarouselResourcesEn } from "@/src/homepage/Flyers/FlyerCarousel.Resources.En";
import * as stylex from "@stylexjs/stylex";

import {
    ComposableSpacing,
    type OmitStyles,
    type QueryLgMax,
    type QueryMdMax,
    type QuerySmMax,
    type QueryXsMax,
    colors,
    globalLimitStyles,
} from "../../../styles/tokens.stylex";
import { FlyerSectionItem, type IFlyerSectionItem } from "./FlyerSectionItem";

export interface IFlyersCarouselV2 {
    items: IFlyerSectionItem[];
    carouselTelemetryCustom?: ITelemetryCustom;
}

const useInfiniteCarousel = (items: IFlyerSectionItem[], animationTimeMS: number) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const timeoutRef = useRef<number | null>(null);

    const next = useCallback(() => {
        if (!isAnimating) {
            setIsAnimating(true);
            setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(() => setIsAnimating(false), animationTimeMS);
        }
    }, [isAnimating, items.length]);

    const prev = useCallback(() => {
        if (!isAnimating) {
            setIsAnimating(true);
            setCurrentIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(() => setIsAnimating(false), animationTimeMS);
        }
    }, [isAnimating, items.length]);

    const visibleItemsArray = useCallback(() => {
        const result: Array<OmitStyles<IFlyerSectionItem>> = items.map((x) => ({ ...x }));

        for (let i = 0; i < 3; i++) {
            const index = (currentIndex + i) % items.length;
            result[index].styleX =
                i == 2 ? styles.rightElement : i == 1 ? styles.centerElement : styles.leftElement;
            result[index].isCenterItem = i == 1;
            result[index].isHidden = false;
        }

        return result;
    }, [currentIndex, items]);

    return { next, prev, visibleItemsArray, isAnimating, currentIndex };
};

const ariaFlyerId = "flyer-aria";

const FlyersCarousel: FC<IFlyersCarouselV2> = (props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const ulListRef = useRef<HTMLUListElement>(null);
    const [dynamicHeight, setDynamicHeight] = useState<number | null>(null);
    const resources = languageSelector(FlyerCarouselResources, FlyerCarouselResourcesEn);
    const { next, prev, visibleItemsArray, isAnimating } = useInfiniteCarousel(props.items, 1400);
    const { isMobile } = useViewportInfoContext();

    const onClickArrow = (isRightClick: boolean) => {
        if (props.carouselTelemetryCustom) {
            sendTelemetryEvent({ ...props.carouselTelemetryCustom, param1: `${isRightClick ? 4 : 3}` });
        }
    };

    useEffect(() => {
        if (!props.items?.length) {
            return;
        }

        let timeoutId: number | null = null;
        let lastHeight = 0;

        const calculateMaxHeight = () => {
            if (!ulListRef.current) return;

            const children = Array.from(ulListRef.current.children) as HTMLElement[];
            let maxHeight = 0;

            children.forEach((child) => {
                const height = child.offsetHeight;
                maxHeight = Math.max(maxHeight, height);
            });

            if (maxHeight > 0 && maxHeight !== lastHeight) {
                lastHeight = maxHeight;

                if (timeoutId) {
                    window.clearTimeout(timeoutId);
                }

                timeoutId = window.setTimeout(() => {
                    if (!ulListRef.current) return;

                    const children = Array.from(ulListRef.current.children) as HTMLElement[];
                    let finalMaxHeight = 0;

                    children.forEach((child) => {
                        finalMaxHeight = Math.max(finalMaxHeight, child.offsetHeight);
                    });

                    if (finalMaxHeight > 0) {
                        const heightWithBuffer = finalMaxHeight * 1.2;
                        setDynamicHeight(heightWithBuffer);
                    }
                }, 500);
            }
        };

        const observer = new ResizeObserver(() => {
            calculateMaxHeight();
        });

        if (ulListRef.current) {
            Array.from(ulListRef.current.children).forEach((child) => {
                observer.observe(child);
            });
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            observer.disconnect();
        };
    }, [props.items]);

    if (!props.items?.length) {
        return;
    }

    const renderDescription = () => {
        return (
            <div {...stylex.props(styles.flyersDescription)}>
                {visibleItemsArray().map((item) => (
                    <div
                        key={item.id}
                        {...stylex.props(
                            styles.descriptionContent,
                            item.isCenterItem && styles.descriptionVisible,
                        )}
                        aria-labelledby={`${ariaFlyerId}-${item.id}`}
                    >
                        <Typography fontStyle="hXS" styleX={styles.text} aria-hidden="true">
                            {item.brandName}
                        </Typography>
                        <Typography
                            fontStyle="BodyL"
                            styleX={globalLimitStyles.TextTwoLinesCap}
                            aria-hidden="true"
                        >
                            {item.flyerSeoDescription}
                        </Typography>
                        <Typography fontStyle="BodyM" styleX={styles.validityTitle} aria-hidden="true">
                            {item.validityTitle}
                        </Typography>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div ref={containerRef} {...stylex.props(styles.carouselContainer)}>
            <ul
                ref={ulListRef}
                {...stylex.props(styles.carouselList)}
                style={dynamicHeight ? { height: `${dynamicHeight}px` } : undefined}
            >
                {visibleItemsArray().map((flyer) => (
                    <li key={flyer.id} {...stylex.props(styles.carouselItem, flyer.styleX)}>
                        <FlyerSectionItem
                            {...flyer}
                            isNewFlyer={flyer.isNewFlyer}
                            newFlyerLabel={resources.newFlyerLabel}
                            isMobile={isMobile}
                        />
                    </li>
                ))}
            </ul>
            {renderDescription()}
            <button
                {...stylex.props(styles.carouselArrow, styles.carouselArrowLeft)}
                onClick={() => {
                    if (isAnimating) {
                        return;
                    }
                    onClickArrow(false);
                    prev();
                }}
                role="button"
                disabled={isAnimating}
            >
                <ChevronLeft />
            </button>
            <button
                {...stylex.props(styles.carouselArrow, styles.carouselArrowRight)}
                onClick={() => {
                    if (isAnimating) {
                        return;
                    }
                    onClickArrow(true);
                    next();
                }}
                role="button"
                disabled={isAnimating}
            >
                <ChevronRight />
            </button>
        </div>
    );
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const smMax: QuerySmMax = "@media (max-width: 989px)";
const mdMax: QueryMdMax = "@media (max-width: 1239px)";
const lgMax: QueryLgMax = "@media (max-width: 1599px)";

const styles = stylex.create({
    carouselContainer: {
        overflow: "hidden",
        position: "relative",
        height: "100%",
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 0,
        width: { default: "90%", [xsMax]: "100%" },
    },

    carouselList: {
        overflow: "visible",
        position: "relative",
        height: {
            default: "26vw",
            [lgMax]: "40vw",
            [mdMax]: "37vw",
            [smMax]: "33vw",
            [xsMax]: "70vw",
        },
    },

    carouselItem: {
        opacity: 0,
        position: "absolute",
        transform: "translateX(-50%)",
        transitionDuration: "1.4s, 0.5s",
        transitionProperty: "all, opacity",
        transitionTimingFunction: "ease-out",
        zIndex: 0,
        bottom: "10%",
        left: "50%",
        width: {
            default: "max-content",
            [smMax]: "33vw",
        },
    },

    leftElement: {
        filter: "brightness(0.5)",

        opacity: 0.7,
        zIndex: 1,
        left: { default: "22%", [xsMax]: "17%" },
    },

    centerElement: {
        opacity: 1,
        scale: 1.1,
        zIndex: 3,
        left: "53%",
    },

    rightElement: {
        filter: "brightness(0.5)",

        opacity: 0.7,
        zIndex: 2,
        left: { default: "80%", [xsMax]: "83%" },
    },

    carouselArrow: {
        strokeWidth: 2,
        color: colors.fg_Primary,
        opacity: { default: 0.5, ":hover": 1, ":focus": 1 },
        position: "absolute",
        height: 32,
        top: { default: "50%", [smMax]: "90%" },
        width: 32,
    },
    carouselArrowLeft: {
        left: { default: "0", [smMax]: "3%" },
    },
    carouselArrowRight: {
        right: { default: "0", [smMax]: "3%" },
    },

    flyersDescription: {
        position: "relative",
        height: "90px",
        marginTop: ComposableSpacing.s8,
        width: "100%",
    },

    descriptionContent: {
        alignItems: "center",
        display: "flex",
        filter: "blur(1px)",
        flexDirection: "column",
        justifyContent: "center",
        opacity: 0,
        position: "absolute",
        textAlign: "center",
        transitionDuration: "1s",
        transitionProperty: "opacity,filter",
        transitionTimingFunction: "ease-out",
        height: "100%",
        top: 0,
        width: "100%",
    },

    descriptionVisible: {
        paddingBlock: "0",
        paddingInline: "10%",
        filter: "none",
        opacity: 1,
    },

    validityTitle: {
        color: colors.fg_Secondary,
    },

    text: {
        fontWeight: "bold",
    },
});

export default FlyersCarousel;
